import { Component, Input, OnInit, OnDestroy, OnChanges, SimpleChanges, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser, CommonModule } from '@angular/common';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatButtonModule } from '@angular/material/button';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { AnalyticsService } from '../../../../services/analytics.service';
import { AnalyticsFilters, MarketSummaryResponse } from '../../../../models/analytics.models';

/**
 * Market Summary Dashboard Component
 * Displays comprehensive overview with all metrics, trends, and top categories/locations
 * SSR-safe: Only renders charts in browser context
 */
@Component({
  selector: 'app-market-summary-dashboard',
  standalone: true,
  imports: [CommonModule, MatProgressSpinnerModule, MatCardModule, MatChipsModule, MatButtonModule, TranslocoModule],
  templateUrl: './market-summary-dashboard.component.html',
  styleUrls: ['./market-summary-dashboard.component.scss'],
})
export class MarketSummaryDashboardComponent implements OnInit, OnDestroy, OnChanges {
  private destroy$ = new Subject<void>();
  private readonly platformId = inject(PLATFORM_ID);
  readonly isBrowser = isPlatformBrowser(this.platformId);

  @Input() filters!: AnalyticsFilters;

  // Summary data
  summaryData: MarketSummaryResponse | null = null;

  loading = false;
  error: string | null = null;

  // Show More functionality
  showAllCategories = false;
  showAllPositions = false;
  showAllLocations = false;
  readonly initialRowsToShow = 10;

  constructor(private analyticsService: AnalyticsService, private translocoService: TranslocoService) {}

  ngOnInit(): void {
    // Only load data on browser
    if (this.isBrowser && this.filters) {
      this.loadSummaryData();
    }

    // Subscribe to language changes
    // This component uses template methods for translation, so no re-transformation needed
    // But we subscribe to ensure change detection runs when language changes
    this.translocoService.langChanges$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        // Trigger change detection for template method re-evaluation
        // Template methods like translateCategory(), translatePosition() will use new language
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Reload data when filters change
    if (changes['filters'] && !changes['filters'].firstChange && this.isBrowser) {
      this.loadSummaryData();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Load summary data from API
   */
  private loadSummaryData(): void {
    if (!this.filters) {
      return;
    }

    this.loading = true;
    this.error = null;

    this.analyticsService
      .getMarketSummary(this.filters)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.summaryData = data;
          this.loading = false;
        },
        error: (err) => {
          console.error('Summary data error:', err);
          this.error = this.translocoService.translate('analytics.errorLoadSummaryData');
          this.loading = false;
        },
      });
  }

  /**
   * Get CSS class for trend indicator
   */
  getTrendClass(trend: 'increasing' | 'stable' | 'decreasing'): string {
    switch (trend) {
      case 'increasing':
        return 'trend-up';
      case 'decreasing':
        return 'trend-down';
      case 'stable':
        return 'trend-stable';
      default:
        return 'trend-stable';
    }
  }

  /**
   * Get icon for trend indicator
   */
  // Returns a FontAwesome class suffix (e.g. 'fa-arrow-up'). Consumers render
  // with <i class="fas {{ getTrendIcon(...) }}"></i>.
  getTrendIcon(trend: 'increasing' | 'stable' | 'decreasing'): string {
    switch (trend) {
      case 'increasing':
        return 'fa-arrow-up';
      case 'decreasing':
        return 'fa-arrow-down';
      case 'stable':
        return 'fa-minus';
      default:
        return 'fa-minus';
    }
  }

  /**
   * Get CSS class for interpretation badge
   */
  getInterpretationClass(interpretation: string): string {
    switch (interpretation) {
      case 'supply_exceeds_demand':
        return 'badge-green';
      case 'balanced':
        return 'badge-blue';
      case 'demand_exceeds_supply':
        return 'badge-orange';
      default:
        return 'badge-gray';
    }
  }

  /**
   * Get display text for interpretation
   */
  getInterpretationText(interpretation: string): string {
    return this.translocoService.translate(`analytics.interpretationValues.${interpretation}`);
  }

  /**
   * Translate category name
   */
  translateCategory(categoryName: string): string {
    return this.translocoService.translate(`jobCategories.${categoryName}`);
  }

  /**
   * Translate position name
   */
  translatePosition(positionName: string): string {
    return this.translocoService.translate(`jobPositions.${positionName}`);
  }

  /**
   * Format location name
   */
  formatLocation(city: string, country: string): string {
    // Translate location name if it's a special key like "Others"
    const cityName = city === 'Others' ? this.translocoService.translate(`locations.${city}`) : city;
    return country ? `${cityName}, ${country}` : cityName;
  }

  /**
   * Get displayed categories based on show more state
   */
  get displayedCategories() {
    if (!this.summaryData?.topCategories) return [];
    return this.showAllCategories ? this.summaryData.topCategories : this.summaryData.topCategories.slice(0, this.initialRowsToShow);
  }

  /**
   * Get displayed positions based on show more state
   */
  get displayedPositions() {
    if (!this.summaryData?.topPositions) return [];
    return this.showAllPositions ? this.summaryData.topPositions : this.summaryData.topPositions.slice(0, this.initialRowsToShow);
  }

  /**
   * Get displayed locations based on show more state
   */
  get displayedLocations() {
    if (!this.summaryData?.topLocations) return [];
    return this.showAllLocations ? this.summaryData.topLocations : this.summaryData.topLocations.slice(0, this.initialRowsToShow);
  }

  /**
   * Check if show more button should be visible for categories
   */
  get shouldShowMoreCategories(): boolean {
    return !!this.summaryData?.topCategories && this.summaryData.topCategories.length > this.initialRowsToShow;
  }

  /**
   * Check if show more button should be visible for positions
   */
  get shouldShowMorePositions(): boolean {
    return !!this.summaryData?.topPositions && this.summaryData.topPositions.length > this.initialRowsToShow;
  }

  /**
   * Check if show more button should be visible for locations
   */
  get shouldShowMoreLocations(): boolean {
    return !!this.summaryData?.topLocations && this.summaryData.topLocations.length > this.initialRowsToShow;
  }

  /**
   * Toggle show all categories
   */
  toggleCategories(): void {
    this.showAllCategories = !this.showAllCategories;
  }

  /**
   * Toggle show all positions
   */
  togglePositions(): void {
    this.showAllPositions = !this.showAllPositions;
  }

  /**
   * Toggle show all locations
   */
  toggleLocations(): void {
    this.showAllLocations = !this.showAllLocations;
  }
}
